import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  inject,
} from '@angular/core';
import type { FormControl } from '@angular/forms';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
// eslint-disable-next-line local-rules/validate-freelancer-imports
import {
  CountryCode,
  type Country,
} from '@freelancer/datastore/collections/countries';
import { FlagComponent } from '@freelancer/ui/flag/flag.component';
import { ValidationErrorComponent } from '@freelancer/ui/validation-error';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy } from '@ngneat/until-destroy';
import {
  parsePhoneNumberFromString,
  type CountryCode as LibPhoneCountryCode,
} from 'libphonenumber-js';
import type { Subscription } from 'rxjs';
import { Focus } from '../focus';
import { trackById } from '../helpers/helpers';
import { IconModule, IconSize } from '../icon';
import { AutoFocusOption } from '../input';

@UntilDestroy({ className: 'PhoneInputComponent' })
@Component({
  standalone: true,
  imports: [
    FlagComponent,
    IconModule,
    ReactiveFormsModule,
    ValidationErrorComponent,
  ],
  selector: 'fl-phone-input',
  template: `
    <div class="Container">
      <!-- Flag & Country Selector -->
      <div
        class="FlagSelect"
        (click)="showDropdown = !showDropdown"
      >
        @if (selectedCountry) {
          <fl-flag
            class="mr-xxxsmall"
            [countryCode]="selectedCountry.code"
            [country]="selectedCountry.name"
          ></fl-flag>
        }
        <fl-icon
          class="Chevron"
          [name]="showDropdown ? 'ui-chevron-up' : 'ui-chevron-down'"
          [useIconFont]="true"
          [fillIconFont]="true"
          [size]="IconSize.MID"
        ></fl-icon>
      </div>

      <!-- Country Dropdown -->
      @if (showDropdown) {
        <div class="Dropdown">
          @if (displayCountriesOptions; as countries) {
            @for (country of countries; track trackById($index, country)) {
              <div
                class="Dropdown-item"
                (click)="selectCountry(country)"
              >
                <fl-flag
                  class="mr-xxsmall"
                  [countryCode]="country.code"
                  [country]="country.name"
                ></fl-flag>
                <p i18n="phone-input-country-name">
                  {{ country.name }} (+{{ country.phoneCode }})
                </p>
              </div>
            }
          }
        </div>
      }

      <!-- Phone Number Input -->
      <div class="PhoneInputContainer">
        <label
          class="Label"
          [class.IsInputEmpty]="currentInputValue === ''"
          i18n="phone-input-label"
        >
          Phone number
        </label>
        <input
          #phoneInput
          class="PhoneInput"
          type="tel"
          [formControl]="formGroup.controls.displayPhoneNumber"
          (keypress)="onKeyPress($event)"
          (input)="onInput($event)"
          (paste)="onPaste($event)"
        />
      </div>
    </div>
    <div class="Error">
      @if (invalidControl) {
        <fl-validation-error
          [control]="invalidControl"
          [showIcon]="false"
        ></fl-validation-error>
      }
    </div>
  `,
  styleUrls: ['./phone-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhoneInputComponent
  implements AfterViewInit, OnInit, OnDestroy, OnChanges
{
  private changeDetectorRef = inject(ChangeDetectorRef);
  private focus = inject(Focus);

  IconSize = IconSize;
  isDefined = isDefined;
  trackById = trackById;

  @Input() countriesOptions: readonly Country[] = [];
  @Input() formGroup: FormGroup<{
    countryCode: FormControl<Country | undefined>;
    phoneNumber: FormControl<string>;
    displayPhoneNumber: FormControl<string>;
  }>;
  /** Allows the input to auto focus */
  @Input() autoFocus = AutoFocusOption.NONE;
  /** Priority countries to show for country select */
  @Input() countryPriority: string[] = [
    CountryCode.US,
    CountryCode.CA,
    CountryCode.NZ,
    CountryCode.AU,
    CountryCode.GB,
  ];

  @ViewChild('phoneInput', { static: false })
  phoneInput: ElementRef<HTMLInputElement>;

  selectedCountry?: Country;
  showDropdown = false;
  invalidControl: FormControl<Country | string | undefined> | undefined;
  sortedCountriesOptions: readonly Country[] = [];
  displayCountriesOptions: readonly Country[] = [];
  currentInputValue = '';

  // Set on initialization when a country is already provided in the control
  userCountry: Country;

  private phoneNumberSubscription: Subscription | undefined;
  private controlStatusesSubscription: Subscription | undefined;

  ngOnInit(): void {
    this.currentInputValue =
      this.formGroup.controls.displayPhoneNumber.value ?? '';

    // Handle phone number changes and country matching
    this.phoneNumberSubscription =
      this.formGroup.controls.displayPhoneNumber.valueChanges.subscribe(
        value => {
          this.currentInputValue = value ?? '';
          this.changeDetectorRef.markForCheck();

          if (!value?.startsWith('+')) {
            this.formGroup.controls.phoneNumber.setValue('');
            return;
          }

          // Update the actual phone number control without country code, using libphonenumber
          const phoneNumber = parsePhoneNumberFromString(value);
          if (phoneNumber) {
            this.formGroup.controls.phoneNumber.setValue(
              phoneNumber.nationalNumber,
            );
          } else if (
            this.selectedCountry &&
            value.startsWith(`+${this.selectedCountry.phoneCode}`)
          ) {
            const numberWithoutCountryCode = value.slice(
              (this.selectedCountry.phoneCode?.toString() ?? '').length + 1,
            );
            this.formGroup.controls.phoneNumber.setValue(
              numberWithoutCountryCode,
            );
          }
        },
      );

    this.controlStatusesSubscription = this.formGroup.statusChanges
      .pipe()
      .subscribe(() => {
        this.validateCountryCode();
        this.invalidControl = [
          this.formGroup.controls.countryCode,
          this.formGroup.controls.phoneNumber,
          this.formGroup.controls.displayPhoneNumber,
        ].find(
          control => control.invalid && (control.touched || control.dirty),
        );

        this.invalidControl?.markAsDirty();

        // Trigger change detection to update the view
        this.changeDetectorRef.markForCheck();
      });

    // Initialize the selected country based on
    // the initial value set in formGroup.countryCode or first in the list
    this.initializeCountry();
    this.sortCountriesOptions();
  }

  ngAfterViewInit(): void {
    this.handleAutoFocus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.countriesOptions?.currentValue) {
      this.initializeCountry();
      this.sortCountriesOptions();
    }

    if (changes.autoFocus?.currentValue && this.phoneInput) {
      // If autoFocus is set, focus the phone input
      this.handleAutoFocus();
    }
  }

  handleAutoFocus(): void {
    if (this.autoFocus !== AutoFocusOption.NONE) {
      this.focus.focusElement(this.phoneInput, {
        allowMobile: this.autoFocus === AutoFocusOption.ALWAYS,
      });
    }
  }

  ngOnDestroy(): void {
    if (this.phoneNumberSubscription) {
      this.phoneNumberSubscription.unsubscribe();
    }
    if (this.controlStatusesSubscription) {
      this.controlStatusesSubscription.unsubscribe();
    }
  }

  initializeCountry(): void {
    if (this.formGroup.controls.countryCode.value) {
      // Set the default country if it's set
      const matchedCountry = this.displayCountriesOptions.find(
        country =>
          country.code === this.formGroup.controls.countryCode.value?.code,
      );
      if (matchedCountry) {
        this.selectCountry(matchedCountry, false);
      }

      if (this.userCountry === undefined && matchedCountry) {
        this.userCountry = matchedCountry;
      }
    } else if (this.sortedCountriesOptions.length > 0) {
      // Default to the first country in the list if no default country is given
      this.selectCountry(this.sortedCountriesOptions[0], false);
    }
  }

  selectCountry(country: Country, emitEvent: boolean = true): void {
    const phoneNumber = this.phoneInput?.nativeElement?.value ?? '';
    const newCountryCode = country.phoneCode?.toString() ?? '';

    if (!phoneNumber.startsWith(`+${newCountryCode}`)) {
      let phoneNumberWithoutCountryCode = '';
      if (phoneNumber.startsWith('+')) {
        // Use libphonenumber to extract national number properly
        const parsed = parsePhoneNumberFromString(phoneNumber);
        if (parsed) {
          phoneNumberWithoutCountryCode = parsed.nationalNumber;
        } else {
          // Fallback to original logic
          const valueAfterPlus = phoneNumber.slice(1);
          const currentCountryCode =
            this.selectedCountry?.phoneCode?.toString();

          phoneNumberWithoutCountryCode =
            currentCountryCode && valueAfterPlus.startsWith(currentCountryCode)
              ? valueAfterPlus.slice(currentCountryCode.length)
              : valueAfterPlus;
        }
      }
      const newValue = `+${newCountryCode}${phoneNumberWithoutCountryCode}`;
      this.currentInputValue = newValue;
      this.formGroup.controls.displayPhoneNumber.setValue(newValue, {
        emitEvent,
      });
    } else {
      this.currentInputValue = phoneNumber;
    }

    this.selectedCountry = country;
    this.formGroup.controls.countryCode.setValue(country, { emitEvent });
    this.showDropdown = false;
    this.invalidControl = undefined;

    this.changeDetectorRef.markForCheck();
    this.phoneInput?.nativeElement?.focus();
  }

  checkCountryCode(value: string): void {
    // First check if the current selected country matches the input
    // to avoid switching countries unnecessarily (e.g., between US and Canada which both use +1)
    if (
      this.selectedCountry &&
      value.startsWith(`+${this.selectedCountry.phoneCode}`)
    ) {
      return;
    }

    const matchedCountry = this.sortedCountriesOptions.find((c: Country) =>
      value.startsWith(`+${c.phoneCode}`),
    );

    if (matchedCountry && matchedCountry !== this.selectedCountry) {
      this.selectCountry(matchedCountry);
    }
  }

  validateCountryCode(): void {
    const displayValue = this.formGroup.controls.displayPhoneNumber.value ?? '';
    const control = this.formGroup.controls.countryCode;

    const isValid =
      this.selectedCountry &&
      displayValue.startsWith(`+${this.selectedCountry.phoneCode}`);

    if (!isValid && displayValue.length > 0) {
      const existingErrors = control.errors ?? {};
      if (!existingErrors.invalidCountryCode) {
        control.setErrors({
          ...existingErrors,
          invalidCountryCode: $localize`Please enter a valid country code.`,
        });
      }
    }
  }

  /** Lower = higher priority. Codes not in `countryPriority` sort after all listed. */
  private countryPrioritySortKey(code: string): number {
    const i = this.countryPriority.indexOf(code);
    return i === -1 ? this.countryPriority.length : i;
  }

  sortCountriesOptions(): void {
    /**
     * Sort countries based on the priority list and phone code length so when checking the country code inputted,
     * the most relevant countries are at the top of the list and we look for the longest phone code match first.
     */
    this.sortedCountriesOptions = [...this.countriesOptions].sort(
      (a: Country, b: Country) => {
        // Prioritize the user's country first
        if (
          this.userCountry &&
          (a.code === this.userCountry.code || b.code === this.userCountry.code)
        ) {
          return a.code === this.userCountry.code ? -1 : 1;
        }

        // Prioritize countries based on the priority list (array order; not listed → last)
        if (
          this.countryPriority.some(countryCode =>
            [a.code, b.code].includes(countryCode),
          )
        ) {
          return (
            this.countryPrioritySortKey(a.code) -
            this.countryPrioritySortKey(b.code)
          );
        }

        // If both countries are not in the priority list, sort by phone code length
        return (
          (b.phoneCode?.toString().length ?? 0) -
          (a.phoneCode?.toString().length ?? 0)
        );
      },
    );

    // Priority list first (same order as `countryPriority`), then the rest
    this.displayCountriesOptions = [...this.countriesOptions].sort(
      (a: Country, b: Country) =>
        this.countryPrioritySortKey(a.code) -
        this.countryPrioritySortKey(b.code),
    );
  }

  // We can't use input type="number" because it doesn't allow the user to enter a + sign at the beginning
  // onKeyPress is used to prevent the user from entering invalid characters into the phone number input
  onKeyPress(event: KeyboardEvent): void {
    this.showDropdown = false;
    const input = event.target as HTMLInputElement;
    const char = event.key;
    const currentValue = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Allow control keys (enter, backspace, delete, tab, arrow keys, etc.)
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Enter',
      ].includes(char)
    ) {
      return;
    }

    // Allow '+' at the beginning (position 0) or if value is empty
    if (char === '+' && (cursorPosition === 0 || currentValue === '')) {
      return;
    }

    // Allow numbers (0-9)
    if (char >= '0' && char <= '9') {
      return;
    }

    // Block all other characters
    event.preventDefault();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    this.showDropdown = false;
    const input = event.target as HTMLInputElement;
    const pastedText = event.clipboardData?.getData('text/plain') || '';

    if (!pastedText.trim()) {
      return;
    }

    const formattedValue = this.formatPastedPhoneNumber(pastedText);
    this.formGroup.controls.displayPhoneNumber.setValue(formattedValue, {
      emitEvent: false,
    });
    this.currentInputValue = formattedValue;
    input.setSelectionRange(formattedValue.length, formattedValue.length);

    this.checkCountryCode(formattedValue);
    this.changeDetectorRef.markForCheck();
  }

  private formatPastedPhoneNumber(pastedText: string): string {
    const cleaned = pastedText.replace(/[^\d+]/g, '');

    // If number already starts with the selected country code, use it
    if (
      this.selectedCountry &&
      this.selectedCountry.phoneCode &&
      cleaned.startsWith(this.selectedCountry.phoneCode.toString())
    ) {
      const phoneNumber = parsePhoneNumberFromString(
        cleaned,
        this.selectedCountry?.code as LibPhoneCountryCode | undefined,
      );
      if (phoneNumber) {
        return `+${phoneNumber.countryCallingCode}${phoneNumber.nationalNumber}`;
      }
    }

    // If already international format, let libphonenumber format it
    if (cleaned.startsWith('+')) {
      const phoneNumber = parsePhoneNumberFromString(cleaned);
      if (phoneNumber) {
        return `+${phoneNumber.countryCallingCode}${phoneNumber.nationalNumber}`;
      }
      // If parsing fails, return the cleaned number as is
      return cleaned;
    }

    // Handle local numbers using libphonenumber's built-in default country support
    const localPhoneNumber = parsePhoneNumberFromString(
      cleaned,
      this.selectedCountry?.code as LibPhoneCountryCode | undefined,
    );

    // If it's a valid local number for the selected country, use it
    if (localPhoneNumber?.isValid()) {
      return `+${localPhoneNumber.countryCallingCode}${localPhoneNumber.nationalNumber}`;
    }

    // If it's not valid locally, check if it's a valid international number missing the +
    const intlPhoneNumber = parsePhoneNumberFromString(`+${cleaned}`);
    if (intlPhoneNumber?.isValid()) {
      return `+${intlPhoneNumber.countryCallingCode}${intlPhoneNumber.nationalNumber}`;
    }

    // Fallback: If libphonenumber was able to parse it locally even if incomplete, use it
    if (localPhoneNumber) {
      return `+${localPhoneNumber.countryCallingCode}${localPhoneNumber.nationalNumber}`;
    }

    // Fallback: If parsing fails (e.g. invalid/incomplete number pasted), use the selected country code
    if (this.selectedCountry) {
      return `+${this.selectedCountry.phoneCode}${cleaned}`;
    }

    // Fallback: If all else fails, return the cleaned number with a +
    return `+${cleaned}`;
  }

  onInput(event: Event): void {
    this.showDropdown = false;
    const input = event.target as HTMLInputElement;
    let { value } = input;

    if (value && !value.startsWith('+')) {
      value = `+${value}`;
      this.formGroup.controls.displayPhoneNumber.setValue(value, {
        emitEvent: false,
      });
    }

    // If user types just "0" (value is "+0"), add country code
    // No country code starts with 0, so this is safe
    if (value === '+0' && this.selectedCountry) {
      value = `+${this.selectedCountry.phoneCode}0`;
      this.formGroup.controls.displayPhoneNumber.setValue(value, {
        emitEvent: false,
      });
    }

    // Format using libphonenumber if we have a value
    if (value && value !== '+') {
      const formattedValue = this.formatPhoneNumber(value);
      if (formattedValue !== value) {
        const cursorPosition = input.selectionStart || 0;
        this.formGroup.controls.displayPhoneNumber.setValue(formattedValue, {
          emitEvent: false,
        });
        const newPosition = Math.min(cursorPosition, formattedValue.length);
        input.setSelectionRange(newPosition, newPosition);
        value = formattedValue;
      }
    }

    // Clear the error display when the user starts typing again after an error has been shown
    if (this.invalidControl) {
      this.invalidControl = undefined;
    }

    this.currentInputValue = value;
    this.checkCountryCode(value);
    this.changeDetectorRef.markForCheck();
  }

  private formatPhoneNumber(value: string): string {
    if (!value || value === '' || value === '+') {
      return value || '+';
    }

    const phoneNumber = parsePhoneNumberFromString(
      value,
      this.selectedCountry?.code as LibPhoneCountryCode | undefined,
    );
    if (phoneNumber) {
      return `+${phoneNumber.countryCallingCode}${phoneNumber.nationalNumber}`;
    }

    // If parsing fails, ensure it starts with +
    if (!value.startsWith('+')) {
      return `+${value.replace(/[^\d]/g, '')}`;
    }

    return value;
  }
}
